import React, { useState, useEffect } from "react";
import "./CalendarScreen.css"; // File per lo stile del calendario
import { getRange } from '../../api/event';
import { useAuth } from "../../context/authContext";
import { useLoading } from "../../context/loadingContext";
import { Button } from "../../component/form/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { LoadingScreenLocal } from "../other/LoadingScreen";
import { useNavigate } from "react-router-dom";
import { isSuccessCode } from "../../helpers/apiResponse";
import { CalendarEvent } from "../../types/api/event.type";

const Calendar: React.FC = () => {
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [isLoading, setIsLoading] = useState(false);

    const { association } = useAuth();
    const { setLoadingStatus, loading } = useLoading();
    const nav = useNavigate();

    // Funzione per formattare la data
    const formatDate = (date: Date): string => {
        return date.toISOString().split("T")[0];
    };

    // Calcolo dell'inizio e della fine del mese
    const getMonthRange = (date: Date) => {
        const start = new Date(date.getFullYear(), date.getMonth(), 1);
        const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        return { start, end };
    };

    // Fetch degli eventi con la query getRange
    const fetchEvents = async (startDate: Date, endDate: Date) => {
        setIsLoading(true);
        const request = await getRange(startDate, endDate);
        if (isSuccessCode(request.error_code)) {
            setEvents(request.data);
        }
        setIsLoading(false);
    };

    // Caricamento iniziale e cambio mese
    useEffect(() => {
        const { start, end } = getMonthRange(currentDate);
        fetchEvents(start, end);
    }, [currentDate]);

    // Cambia mese
    const changeMonth = (offset: number) => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1));
    };

    const generateCalendarDays = () => {
        const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
        const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
        const calendarDays: JSX.Element[] = [];
        const today = new Date(); // Otteniamo la data di oggi
        const todayString = today.toLocaleDateString('en-CA'); // Formato yyyy-mm-dd in base al locale (CA=Canada)

        // Aggiungi giorni vuoti all'inizio della settimana
        for (let i = 0; i < firstDayOfMonth; i++) {
            calendarDays.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
        }

        // Aggiungi i giorni del mese
        for (let day = 1; day <= daysInMonth; day++) {
            const currentDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
            const dayString = currentDay.toLocaleDateString('en-CA'); // Formato yyyy-mm-dd in base al locale

            // Trova tutti gli eventi che sono nel range di date che includono il giorno corrente
            const dayEvents = events.filter((event) => {
                const eventStart = new Date(event.start_at);
                const eventEnd = new Date(event.end_at);

                // Confrontiamo le date senza considerare l'ora, includendo l'evento che inizia lo stesso giorno
                const startDateOnly = eventStart.toLocaleDateString('en-CA');
                const endDateOnly = eventEnd.toLocaleDateString('en-CA');

                return (startDateOnly <= dayString && endDateOnly >= dayString);
            });

            const isToday = dayString === todayString; // Verifica se è il giorno corrente

            calendarDays.push(
                <div
                    key={day}
                    className={`calendar-day ${dayEvents.length > 0 ? 'has-events' : ''} ${isToday ? 'today' : ''}`}
                >
                    <div className="day-number">{day}</div>
                    {dayEvents.length > 0 && (
                        <div className="events-list">
                            {dayEvents.map((event) => (
                                <div key={event.id} className={`event-name ${event.type}`} onClick={() => nav("/report/event/?event_id=" + event.id)}>
                                    {event.title}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        return calendarDays;
    };

    if (loading)
        return (<LoadingScreenLocal />)


    return (
        <div className="calendar-container">
            <div className="calendar-header">
                <Button action={() => changeMonth(-1)} name={<FontAwesomeIcon icon={faArrowLeft} />} />
                <h2>
                    {currentDate.toLocaleString("default", { month: "long" })} {currentDate.getFullYear()}
                </h2>
                <Button action={() => changeMonth(1)} name={<FontAwesomeIcon icon={faArrowRight} />} />
            </div>
            <div className="calendar-body">
                <div className="calendar-grid">
                    {generateCalendarDays()}
                </div>
            </div>
            <div className="calendar-legend">
                <div className="legend-name training">Training</div>
                <div className="legend-name competition">Competition</div>
                <div className="legend-name solo_training">Solo Training</div>
            </div>
        </div>
    );
};

export default Calendar;
